import { head } from 'ramda';

import type {
  CalculationState,
  CompletedCalculationTactic,
  LoadFlowScenarioElementCalculation,
  LoadFlowScenarioResults
} from '../..';
import { isCalculationTacticResultUnavailable } from '../../actions';
import { ActivePower } from '../../domain/ActivePower';
import type { Capacity } from '../../domain/Capacity';
import type { Degree } from '../../domain/Degree';
import type { ElectricLoad } from '../../domain/ElectricLoad';
import type { Percent } from '../../domain/Percent';
import { ReactivePower } from '../../domain/ReactivePower';
import { Voltage } from '../../domain/Voltage';
import type { EvaluatedResult } from '../../evaluation/EvaluatedResult';
import type {
  FunctionalGrid,
  PowerTransformer,
  PowerTransformerEnd
} from '../../grid/FunctionalGrid';
import type { LvLoadFlowSummary } from './lowVoltage/LvLoadFlowSummary';
import type { MvLoadFlowSummary } from './mediumVoltage/MvLoadFlowSummary';
import { getLoadFlowScenarioSummary as getLoadFlowSummary } from './summary';

export type LoadFlowSummary = MvLoadFlowSummary | LvLoadFlowSummary;

export const loadFlowSummary = (
  state: CalculationState
): CalculationState['loadFlowSummary'] => {
  if(state.evaluationRules.type === 'UNAVAILABLE') {
    return { type: 'NO_SUMMARY_AVAILABLE' };
  }
  const calculationTactic = state.calculationTactic.value;
  if (
    isCalculationTacticResultUnavailable(calculationTactic) ||
    state.calculationTactic.current !== 'LOADED' ||
    !(
      state.tacticResultBrowsingFilter.type === 'ALL' ||
      state.tacticResultBrowsingFilter.type === 'ALL_LOAD_FLOW' ||
      state.tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW'
    )
  ) {
    return { type: 'NO_SUMMARY_AVAILABLE' };
  }

  const voltageLevel = calculationTactic?.calculationSetResult.voltageLevel;

  if (
    state.resultsBrowsingMode.matches('BROWSING_SUMMARY') &&
    state.tacticResultBrowsingFilter.type !== 'SELECTED_LOAD_FLOW'
  ) {
    const allLoadFlowScenarioIds =
      state.calculationTactic.value.status === 'Completed'
        ? state.calculationTactic.value.calculationSetResult.calculationScenarios.loadFlow.map(
            lf => lf.scenarioId
          )
        : [];

    return {
      type: 'SUMMARY_AVAILABLE',
      summary: getLoadFlowSummary(
        state.calculationTactic
          .value as unknown as NonNullable<CompletedCalculationTactic>,
        state.evaluationRules.rules,
        allLoadFlowScenarioIds,
        voltageLevel
      )
    };
  }

  if (
    state.resultsBrowsingMode.matches('BROWSING_SUMMARY') &&
    state.tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW'
  ) {
    const selectedScenarioIds = state.tacticResultBrowsingFilter.scenarioIds;
    return {
      type: 'SUMMARY_AVAILABLE',
      summary: getLoadFlowSummary(
        state.calculationTactic
          .value as unknown as NonNullable<CompletedCalculationTactic>,
        state.evaluationRules.rules,
        selectedScenarioIds,
        voltageLevel
      )
    };
  }

  return { type: 'NO_SUMMARY_AVAILABLE' };
};

export type BusbarEvaluatedVoltages = {
  min: EvaluatedResult<Voltage>;
  max: EvaluatedResult<Voltage>;
};

export type BusbarEvaluatedVoltageDiffTransformerEndPercents = {
  min: EvaluatedResult<Percent>;
  max: EvaluatedResult<Percent>;
};

export type EnergyConsumerEvaluatedVoltages = {
  min: EvaluatedResult<Voltage>;
  max: EvaluatedResult<Voltage>;
};
export type AcLineEvaluatedLoading = {
  min: EvaluatedResult<ElectricLoad>;
  max: EvaluatedResult<ElectricLoad>;
};

export type BusbarEvaluatedVoltage = {
  min: EvaluatedResult<Voltage>;
  max: EvaluatedResult<Voltage>;
};

export type EnergyConsumerEvaluatedVoltageDiffTransformerEndPercent = {
  min: EvaluatedResult<Percent>;
  max: EvaluatedResult<Percent>;
};

export type EnergyConsumerEvaluatedVoltageDiffTransformerEnd = {
  min: EvaluatedResult<Voltage>;
  max: EvaluatedResult<Voltage>;
};

export type EnergyConsumerEvaluatedVoltageCosinePhi = {
  min: EvaluatedResult<Degree>;
  max: EvaluatedResult<Degree>;
};

export type MvPowerTransformerEvaluatedLoadingPercents = {
  min: EvaluatedResult<ElectricLoad>;
  max: EvaluatedResult<ElectricLoad>;
};

export type PowerTransformerEvaluatedLoadingPercents = {
  min: EvaluatedResult<ElectricLoad>;
  max: EvaluatedResult<ElectricLoad>;
};

export type TransformerEvaluatedLoading = EvaluatedResult<ElectricLoad>;

export type PowerTransformerEvaluatedAvailableCapacities = {
  min: EvaluatedResult<Capacity>;
  max: EvaluatedResult<Capacity>;
};
export type TransformerEvaluatedAvailableCapacity = EvaluatedResult<Capacity>;

export type TransformerEvaluatedRatedCapacity = EvaluatedResult<Capacity>;

export type PowerTransformerEndVoltageSummary = {
  min: Voltage;
  max: Voltage;
};

export type TwoWindingPowerTransformerVoltageSummary = {
  type: 'TWO_WINDING';
  primary: PowerTransformerEndVoltageSummary;
  secondary: PowerTransformerEndVoltageSummary;
};

export type ThreeWindingPowerTransformerVoltageSummary = {
  type: 'THREE_WINDING';
  primary: PowerTransformerEndVoltageSummary;
  secondary: PowerTransformerEndVoltageSummary;
  tertiary: PowerTransformerEndVoltageSummary;
};

export type PowerTransformerEndsVoltageSummary =
  | TwoWindingPowerTransformerVoltageSummary
  | ThreeWindingPowerTransformerVoltageSummary;
export const getPowerTransformerEndsVoltageSummary = (
  calculationTactic: CompletedCalculationTactic,
  scenariosIds: string[]
): PowerTransformerEndsVoltageSummary => {
  const powerTransformer = head(
    calculationTactic.calculationSetResult.grid.functional.powerTransformers
  ) as unknown as NonNullable<FunctionalGrid['powerTransformers'][number]>;
  const transformerWindingType = parseTransformerWindingType(powerTransformer);
  const powerTransformerEnds =
    calculationTactic.calculationSetResult.grid.functional.powerTransformerEnds.filter(
      powerTransformerEnd =>
        powerTransformer.powerTransformerEnds.includes(
          powerTransformerEnd.objectId
        )
    );
  const _powerTransformerEnds = parsePowerTranformerEnds(
    powerTransformerEnds as unknown as Parameters<
      typeof parsePowerTranformerEnds
    >[number]
  );
  if (transformerWindingType === 'TWO_WINDING') {
    return {
      type: 'TWO_WINDING',
      primary: findPowerTransformerEndMinMaxVoltages(
        calculationTactic.calculationSetResult.calculationScenarios.loadFlow,
        _powerTransformerEnds.primary,
        scenariosIds
      ),
      secondary: findPowerTransformerEndMinMaxVoltages(
        calculationTactic.calculationSetResult.calculationScenarios.loadFlow,
        _powerTransformerEnds.secondary,
        scenariosIds
      )
    };
  }
  return {
    type: 'THREE_WINDING',
    primary: findPowerTransformerEndMinMaxVoltages(
      calculationTactic.calculationSetResult.calculationScenarios.loadFlow,
      _powerTransformerEnds.primary,
      scenariosIds
    ),
    secondary: findPowerTransformerEndMinMaxVoltages(
      calculationTactic.calculationSetResult.calculationScenarios.loadFlow,
      _powerTransformerEnds.secondary,
      scenariosIds
    ),
    tertiary: findPowerTransformerEndMinMaxVoltages(
      calculationTactic.calculationSetResult.calculationScenarios.loadFlow,
      _powerTransformerEnds.teritary as unknown as NonNullable<PowerTransformerEnd>,
      scenariosIds
    )
  };
};

const parseTransformerWindingType = (
  powerTransformer: PowerTransformer
): 'TWO_WINDING' | 'THREE_WINDING' =>
  powerTransformer.powerTransformerEnds.length === 2
    ? 'TWO_WINDING'
    : 'THREE_WINDING';

const parsePowerTranformerEnds = (
  powerTransformerEnds:
    | [PowerTransformerEnd, PowerTransformerEnd, PowerTransformerEnd]
    | [PowerTransformerEnd, PowerTransformerEnd]
) => {
  if (powerTransformerEnds.length === 3) {
    return {
      primary: powerTransformerEnds.filter(end => end.endNumber === 1)[0],
      secondary: powerTransformerEnds.filter(end => end.endNumber === 2)[0],
      teritary: powerTransformerEnds.filter(end => end.endNumber === 3)[0]
    };
  }
  return {
    primary: powerTransformerEnds.filter(end => end.endNumber === 1)[0],
    secondary: powerTransformerEnds.filter(end => end.endNumber === 2)[0]
  };
};

function findPowerTransformerEndMinMaxVoltages(
  loadFlowResults: LoadFlowScenarioResults[],
  powerTransformerEnd: PowerTransformerEnd,
  scenariosIds: string[]
): PowerTransformerEndVoltageSummary {
  const powerTransformerEndLoadFlowResult = loadFlowResults
    .filter(scenario => scenariosIds.includes(scenario.scenarioId))
    .flatMap(scenario => scenario.results)
    .filter(
      gridElementResults =>
        gridElementResults.objectType === 'PowerTransformerEnd' &&
        powerTransformerEnd.objectId === gridElementResults.id
    )
    .map(
      results =>
        (
          results as unknown as Extract<
            LoadFlowScenarioElementCalculation,
            { objectType: 'PowerTransformerEnd' }
          >
        ).result.voltage
    );

  return {
    min: new Voltage(Math.min(...powerTransformerEndLoadFlowResult)),
    max: new Voltage(Math.max(...powerTransformerEndLoadFlowResult))
  };
}

export type EnergyConsumerLoadsSummary = {
  activePowers: { min: ActivePower; max: ActivePower };
  reactivePowers: { min: ReactivePower; max: ReactivePower };
};
export const getEnergyConsumerLoadsSummary = (
  calculationTactic: CompletedCalculationTactic,
  scenariosIds: string[]
): EnergyConsumerLoadsSummary => {
  // From Usser story description: https://github.com/Volue/pg-team-nsa/issues/711
  // When displaying a summary when multiple scenarios are chosen, present the highest and lowest sum of Active Power, with its corresponding sum of Reactive Power.
  const allLoads = scenariosIds.map(scenarioId => {
    const calculationScenario =
      calculationTactic.calculationSetResult.calculationScenarios.loadFlow.find(
        x => x.scenarioId === scenarioId
      );
    if (!calculationScenario) {
      throw new Error(
        `Calculation scenario not found for scenarioId: ${scenarioId}`
      );
    }
    const allActivePowers = calculationScenario.inputs.loads.reduce(
      (sum, load) => sum + load.p,
      0
    );
    const allReactivePowers = calculationScenario.inputs.loads.reduce(
      (sum, load) => sum + load.q,
      0
    );
    return { allActivePowers, allReactivePowers };
  });
  const maxActivePowerLoad = allLoads.reduce((prev, current) => {
    return current.allActivePowers > prev.allActivePowers ? current : prev;
  });
  const minActivePowerLoad = allLoads.reduce((prev, current) => {
    return current.allActivePowers < prev.allActivePowers ? current : prev;
  });

  return {
    activePowers: {
      min: ActivePower.fromWattNumber(
        minActivePowerLoad.allActivePowers
      ).toKilo(),
      max: ActivePower.fromWattNumber(
        maxActivePowerLoad.allActivePowers
      ).toKilo()
    },
    reactivePowers: {
      min: ReactivePower.fromVarNumber(
        minActivePowerLoad.allReactivePowers
      ).toKilo(),
      max: ReactivePower.fromVarNumber(
        maxActivePowerLoad.allReactivePowers
      ).toKilo()
    }
  };
};
